import { createSlice } from '@reduxjs/toolkit';
import initialState, { createDefaultForm } from './initialState';
import {
  getWhatsAppTemplates,
  getWhatsAppTemplate,
  getTemplateVariables,
  createWhatsAppTemplate,
  updateWhatsAppTemplate,
  assignTemplateParameters,
  deleteWhatsAppTemplate,
} from './services';
import { mapTemplateToForm, mapParametersToBindings } from './helpers';

const resetEditTemplateModal = (state) => {
  state.editTemplateModal.isOpen = false;
  state.editTemplateModal.step = 0;
  state.editTemplateModal.isSubmitting = false;
  state.editTemplateModal.isLoadingTemplate = false;
  state.editTemplateModal.isFormDirty = false;
  state.editTemplateModal.templateId = null;
  state.editTemplateModal.form = createDefaultForm();
  state.editTemplateModal.parameters = [];
  state.editTemplateModal.variables = [];
  state.editTemplateModal.updateResponse = null;
  state.editTemplateModal.previewTemplate = null;
  state.editTemplateModal.contentValidationErrors = null;
};

export const WhatsAppTemplatesSlice = createSlice({
  name: 'WhatsAppTemplates',
  initialState,
  reducers: {
    toggleAddTemplateModal(state) {
      state.addTemplateModal.isOpen = !state.addTemplateModal.isOpen;

      if (!state.addTemplateModal.isOpen) {
        state.addTemplateModal.step = 0;
        state.addTemplateModal.isSubmitting = false;
        state.addTemplateModal.form = createDefaultForm();
        state.addTemplateModal.createResponse = null;
        state.addTemplateModal.variables = [];
        state.addTemplateModal.previewTemplate = null;
        state.addTemplateModal.contentValidationErrors = null;
      }
    },
    setAddTemplateModalStep(state, action) {
      state.addTemplateModal.step = action.payload;
    },
    updateAddTemplateForm(state, action) {
      const { field, value } = action.payload;
      state.addTemplateModal.form[field] = value;
      state.addTemplateModal.contentValidationErrors = null;
    },
    updateAddTemplateParameterBinding(state, action) {
      const { key, variableId } = action.payload;
      state.addTemplateModal.form.parameterBindings[key] = variableId;
    },
    openEditTemplateModal(state, action) {
      const template = action.payload;

      state.editTemplateModal.isOpen = true;
      state.editTemplateModal.step = 0;
      state.editTemplateModal.isSubmitting = false;
      state.editTemplateModal.isFormDirty = false;
      state.editTemplateModal.templateId = template.id;
      state.editTemplateModal.form = mapTemplateToForm(template);
      state.editTemplateModal.parameters =
        template.configuration?.parameters || [];
      state.editTemplateModal.updateResponse = null;
      state.editTemplateModal.previewTemplate = null;
      state.editTemplateModal.contentValidationErrors = null;
    },
    closeEditTemplateModal(state) {
      resetEditTemplateModal(state);
    },
    setEditTemplateModalStep(state, action) {
      state.editTemplateModal.step = action.payload;
    },
    updateEditTemplateForm(state, action) {
      const { field, value } = action.payload;
      state.editTemplateModal.isFormDirty = true;
      state.editTemplateModal.form[field] = value;
      state.editTemplateModal.contentValidationErrors = null;
    },
    updateEditTemplateParameterBinding(state, action) {
      const { key, variableId } = action.payload;
      state.editTemplateModal.form.parameterBindings[key] = variableId;
    },
    setAddTemplateContentValidationErrors(state, action) {
      state.addTemplateModal.contentValidationErrors = action.payload;
    },
    setEditTemplateContentValidationErrors(state, action) {
      state.editTemplateModal.contentValidationErrors = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWhatsAppTemplates.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getWhatsAppTemplates.fulfilled, (state, action) => {
      state.isLoading = false;
      state.templates = (
        Array.isArray(action.payload) ? action.payload : []
      ).filter((template) => template.status !== 'DELETED');
    });
    builder.addCase(getWhatsAppTemplates.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(getWhatsAppTemplate.pending, (state) => {
      state.editTemplateModal.isLoadingTemplate = true;
    });
    builder.addCase(getWhatsAppTemplate.fulfilled, (state, action) => {
      state.editTemplateModal.isLoadingTemplate = false;

      if (!state.editTemplateModal.isFormDirty) {
        state.editTemplateModal.form = mapTemplateToForm(action.payload);
      }

      state.editTemplateModal.parameters =
        action.payload?.configuration?.parameters || [];
    });
    builder.addCase(getWhatsAppTemplate.rejected, (state) => {
      state.editTemplateModal.isLoadingTemplate = false;
    });

    builder.addCase(getTemplateVariables.pending, (state) => {
      if (state.addTemplateModal.isOpen) {
        state.addTemplateModal.isSubmitting = true;
      }

      if (state.editTemplateModal.isOpen) {
        state.editTemplateModal.isSubmitting = true;
      }
    });
    builder.addCase(getTemplateVariables.fulfilled, (state, action) => {
      const variables = Array.isArray(action.payload) ? action.payload : [];

      if (state.addTemplateModal.isOpen) {
        state.addTemplateModal.isSubmitting = false;
        state.addTemplateModal.variables = variables;
      }

      if (state.editTemplateModal.isOpen) {
        state.editTemplateModal.isSubmitting = false;
        state.editTemplateModal.variables = variables;
      }
    });
    builder.addCase(getTemplateVariables.rejected, (state) => {
      if (state.addTemplateModal.isOpen) {
        state.addTemplateModal.isSubmitting = false;
      }

      if (state.editTemplateModal.isOpen) {
        state.editTemplateModal.isSubmitting = false;
      }
    });

    builder.addCase(createWhatsAppTemplate.pending, (state) => {
      state.addTemplateModal.isSubmitting = true;
    });
    builder.addCase(createWhatsAppTemplate.fulfilled, (state, action) => {
      state.addTemplateModal.isSubmitting = false;
      state.addTemplateModal.createResponse = action.payload;

      const bindingsFromResponse = mapParametersToBindings(
        action.payload?.parameters
      );

      if (Object.keys(bindingsFromResponse).length) {
        state.addTemplateModal.form.parameterBindings = {
          ...state.addTemplateModal.form.parameterBindings,
          ...bindingsFromResponse,
        };
      }
    });
    builder.addCase(createWhatsAppTemplate.rejected, (state) => {
      state.addTemplateModal.isSubmitting = false;
    });

    builder.addCase(updateWhatsAppTemplate.pending, (state) => {
      state.editTemplateModal.isSubmitting = true;
    });
    builder.addCase(updateWhatsAppTemplate.fulfilled, (state, action) => {
      state.editTemplateModal.isSubmitting = false;
      state.editTemplateModal.updateResponse = action.payload;

      const responseParameters =
        action.payload?.configuration?.parameters ||
        action.payload?.parameters ||
        [];

      if (responseParameters.length) {
        state.editTemplateModal.parameters = responseParameters;
      }

      const bindingsFromResponse = mapParametersToBindings(responseParameters);

      if (Object.keys(bindingsFromResponse).length) {
        state.editTemplateModal.form.parameterBindings = {
          ...state.editTemplateModal.form.parameterBindings,
          ...bindingsFromResponse,
        };
      }
    });
    builder.addCase(updateWhatsAppTemplate.rejected, (state) => {
      state.editTemplateModal.isSubmitting = false;
    });

    builder.addCase(assignTemplateParameters.pending, (state) => {
      if (state.addTemplateModal.isOpen) {
        state.addTemplateModal.isSubmitting = true;
      }

      if (state.editTemplateModal.isOpen) {
        state.editTemplateModal.isSubmitting = true;
      }
    });
    builder.addCase(assignTemplateParameters.fulfilled, (state, action) => {
      if (state.addTemplateModal.isOpen) {
        state.addTemplateModal.isSubmitting = false;
        state.addTemplateModal.previewTemplate = action.payload;
      }

      if (state.editTemplateModal.isOpen) {
        state.editTemplateModal.isSubmitting = false;
        state.editTemplateModal.previewTemplate = action.payload;
      }
    });
    builder.addCase(assignTemplateParameters.rejected, (state) => {
      if (state.addTemplateModal.isOpen) {
        state.addTemplateModal.isSubmitting = false;
      }

      if (state.editTemplateModal.isOpen) {
        state.editTemplateModal.isSubmitting = false;
      }
    });

    builder.addCase(deleteWhatsAppTemplate.fulfilled, (state, action) => {
      state.templates = state.templates.filter(
        (template) => template.id !== action.meta.arg
      );
    });
  },
});

export const {
  toggleAddTemplateModal,
  setAddTemplateModalStep,
  updateAddTemplateForm,
  updateAddTemplateParameterBinding,
  openEditTemplateModal,
  closeEditTemplateModal,
  setEditTemplateModalStep,
  updateEditTemplateForm,
  updateEditTemplateParameterBinding,
  setAddTemplateContentValidationErrors,
  setEditTemplateContentValidationErrors,
} = WhatsAppTemplatesSlice.actions;

export default WhatsAppTemplatesSlice.reducer;
